import type { ReactElement } from 'react'
import { useEffect } from 'react'

import { useRouting } from 'src/routes'
import { useSession } from 'src/hooks/use-session'
import { Redirect } from 'src/routes/Redirect'
import { useUserAnalytics } from 'src/hooks/use-analytics'
import { useDelayedUser } from 'src/hooks/use-user'
import { useProfile } from 'src/hooks/use-profile'
import { FullLoading } from 'src/system/FullLoading'
import { ThrowError } from 'src/system/ErrorBoundary'

export function ProtectedRoute(props: { children: ReactElement }) {
  const { session, user, error, isAuthenticated } = useUserSession()

  const {
    routes: { account, login }
  } = useRouting()

  if (error) return <ThrowError error={error} />
  if (!session) return null
  if (!isAuthenticated) return <Redirect to={login} />

  if (user) {
    const { isAccountLinked, automatch } = user
    const isAutomatchActive = automatch.isActive
    // Normal, fully set-up users: render immediately and never fetch the
    // profile query.
    if (isAccountLinked && isAutomatchActive) return props.children
    // No Steam link at all (pure-FACEIT included): stay locked to the account
    // page, exactly as before.
    if (!isAccountLinked) return <Redirect to={account} />
    // Steam-linked but automatch inactive: allow through only if the user also
    // has a FACEIT link. This needs the separate profile query, so defer it to
    // a child component that only mounts for this edge case.
    return <SteamLinkedFaceitGate>{props.children}</SteamLinkedFaceitGate>
  }

  return <FullLoading />
}

function SteamLinkedFaceitGate(props: { children: ReactElement }) {
  const { profile, loading } = useProfile('cache-and-network')
  const {
    routes: { account }
  } = useRouting()

  if (loading && !profile) return <FullLoading />
  if (profile?.faceit?.id) return props.children
  return <Redirect to={account} />
}

export function useUserSession() {
  const { data, error } = useSession()
  const session = data?.session
  const isAuthenticated = session?.isAuthenticated ?? false
  const { user } = useDelayedUser()
  const setUserAnalytics = useUserAnalytics()
  useEffect(() => {
    setUserAnalytics(user)
  }, [user, setUserAnalytics])
  return { session, user, error, isAuthenticated }
}
